import { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------ */
/*  Icons (inline SVG — no external icon dependency)                   */
/* ------------------------------------------------------------------ */
const Icon = {
  Upload: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
      <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  ),
  Sliders: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
    </svg>
  ),
  Cpu: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </svg>
  ),
  Chart: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 14l3-3 3 3 5-6" />
    </svg>
  ),
  Mail: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  ),
  User: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  ),
  Lock: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  ),
  Eye: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
      <path d="M9.5 9.5a3 3 0 0 0 4.2 4.2" />
    </svg>
  ),
  Spark: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2l1.9 5.6L19.5 9l-4.5 3.3L16.8 18 12 14.7 7.2 18l1.8-5.7L4.5 9l5.6-1.4L12 2z" />
    </svg>
  ),
};

/* ------------------------------------------------------------------ */
/*  AI Testing Flow — the steps shown on the left panel                */
/* ------------------------------------------------------------------ */
const FLOW_STEPS = [
  {
    icon: Icon.Upload,
    title: "Upload Test Suite",
    desc: "Import prompts, datasets & expected outcomes.",
  },
  {
    icon: Icon.Sliders,
    title: "Configure Model",
    desc: "Select the model, params & evaluation criteria.",
  },
  {
    icon: Icon.Cpu,
    title: "Run Evaluation",
    desc: "Execute automated tests across the AI pipeline.",
  },
  {
    icon: Icon.Chart,
    title: "Review Results",
    desc: "Analyze scores, regressions & quality reports.",
  },
];

function FlowPanel() {
  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 p-10 text-white lg:p-12">
      {/* decorative glow */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl" />

      {/* Logo — top left */}
      <img
        src={`${process.env.PUBLIC_URL}/logo.png`}
        alt="Company logo"
        className="absolute left-10 top-10 z-10 h-10 w-auto object-contain lg:left-12 lg:top-12"
      />

      {/* Title + flow (vertically centered) */}
      <div className="relative flex flex-1 flex-col items-center justify-center py-10">
        <h1 className="text-center text-3xl font-bold tracking-tight text-white">
          CortexQA
        </h1>
        <p className="mt-1 text-center text-sm font-medium uppercase tracking-[0.2em] text-brand-200/80">
          AI Testing Server
        </p>
        <p className="mt-3 text-center text-sm text-brand-100/70">
          Four simple steps from suite to report.
        </p>

        <ol className="relative mt-10 w-full max-w-sm space-y-7">
          {/* connecting line */}
          <span className="absolute left-[22px] top-2 bottom-2 w-px bg-white/15" aria-hidden="true" />
          {FLOW_STEPS.map((step, i) => {
            const StepIcon = step.icon;
            return (
              <li
                key={step.title}
                className="relative flex items-start gap-4 animate-fade-in-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <div className="relative z-10 flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur">
                  <StepIcon className="h-5 w-5 text-brand-100" />
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-300 text-[11px] font-bold text-brand-900">
                    {i + 1}
                  </span>
                </div>
                <div className="pt-0.5">
                  <p className="font-semibold leading-tight">{step.title}</p>
                  <p className="mt-0.5 text-sm text-brand-100/70">{step.desc}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Footer */}
      <p className="relative text-xs text-brand-100/60">
        © {new Date().getFullYear()} CortexQA. All rights reserved.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Login form — the right side                                        */
/* ------------------------------------------------------------------ */
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* `notice` says why the login page is back ("Your session has ended…"); it
   goes once the user signs in again. */
function LoginForm({ onLogin, notice = "" }) {
  const [shownNotice, setShownNotice] = useState(notice);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ userid: "", password: "", remember: true });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [buildInfo, setBuildInfo] = useState("");
  const useridRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}buildinfo`);
        const body = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && body?.data?.build) setBuildInfo(body.data.build);
      } catch {
        /* build info is best-effort; leave the line hidden on failure */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Browsers autofill saved credentials after mount without firing onChange,
  // leaving stale values visible after sign-out. Force the DOM inputs empty.
  useEffect(() => {
    const clear = () => {
      if (useridRef.current) useridRef.current.value = "";
      if (passwordRef.current) passwordRef.current.value = "";
    };
    clear();
    const t = setTimeout(clear, 50);
    return () => clearTimeout(t);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setShownNotice("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userid: form.userid.trim(), password: form.password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.data?.authentication !== "success") {
        setErrorMsg(body?.detail || "Unable to login. Please check your credentials.");
        return;
      }
      onLogin?.(body.data);
    } catch (err) {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex h-full items-center justify-center bg-card px-6 py-12 sm:px-12">
      <div className="w-full max-w-sm">
        {/* Mobile brand (hidden on large where the left panel shows) */}
        <div className="mb-8 flex items-center gap-2 lg:hidden">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-700 text-white">
            <Icon.Spark className="h-5 w-5" />
          </div>
          <span className="font-bold text-slate-800">CortexQA</span>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Welcome</h1>
        <p className="mt-1.5 text-sm text-slate-500">
          Sign in to run and monitor your AI test suites.
        </p>

        {shownNotice && (
          <div
            role="status"
            className="mt-6 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800"
          >
            {shownNotice}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" autoComplete="off">
          {/* User ID */}
          <div>
            <label htmlFor="userid" className="mb-1.5 block text-sm font-medium text-slate-700">
              User ID
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                <Icon.User className="h-5 w-5" />
              </span>
              <input
                id="userid"
                name="userid"
                type="text"
                autoComplete="off"
                ref={useridRef}
                required
                value={form.userid}
                onChange={handleChange}
                placeholder="Enter your userid"
                className="block w-full rounded-lg border border-slate-300 bg-card py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="userid" className="mb-1.5 block text-sm font-medium text-slate-700">
              Password
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                <Icon.Lock className="h-5 w-5" />
              </span>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                ref={passwordRef}
                required
                value={form.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="block w-full rounded-lg border border-slate-300 bg-card py-2.5 pl-10 pr-10 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <Icon.EyeOff className="h-5 w-5" /> : <Icon.Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Remember me */}
          {/* <label className="flex select-none items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              name="remember"
              checked={form.remember}
              onChange={handleChange}
              className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600/40"
            />
            Keep me signed in
          </label> */}

          {/* Error message */}
          {errorMsg && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600/40 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
                </svg>
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>

          <div className="flex flex-col items-center gap-1 pt-1">
            {buildInfo && (
              <p className="font-mono text-[11px] text-slate-400">{buildInfo}</p>
            )}
          </div>
        </form>

        {/* Divider */}
        {/* <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          OR
          <span className="h-px flex-1 bg-slate-200" />
        </div> */}

        {/* SSO */}
        {/* <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-card px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
        >
          Continue with SSO
        </button> */}

        {/* <p className="mt-8 text-center text-sm text-slate-500">
          Don&apos;t have an account?{" "}
          <a href="#signup" className="font-semibold text-brand-700 hover:text-brand-800">
            Request access
          </a>
        </p> */}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page layout                                                        */
/* ------------------------------------------------------------------ */
export default function Login({ onLogin, notice = "" }) {
  return (
    <div className="min-h-screen w-full bg-slate-50">
      <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2">
        {/* Left: green flow panel (hidden on small screens) */}
        <div className="hidden lg:block">
          <FlowPanel />
        </div>
        {/* Right: login fields */}
        <LoginForm onLogin={onLogin} notice={notice} />
      </div>
    </div>
  );
}
